import { getDefinition, type DefLookup } from "./catalog";
import { makeDiagnosticCableSpecGetter } from "./diagnostics/sizing";
import { DEFAULT_AUTO_SIZE_VDROP_TARGET } from "./settings/auto-size-vdrop-default";
import type { System } from "./types";

/** Scales the V-drop the engine already computed, which is linear in length.
 *  Re-sizing a per-cable clone would miss the WeakMap caches keyed on the
 *  system *reference*. */

/** How wrong a guessed run is assumed to be. Not from ABYC or ISO — neither says
 *  how far out an estimate runs; 1.5x is the ordinary routing miss. */
export const LENGTH_GUESS_STRETCH = 1.5;

export type LengthSensitivity = {
  /** Fraction of this cable's E-11.14.2.6 allowance used at its stated length;
   *  0 when no current resolves. */
  allowanceUsed: number;
  /** The same fraction at {@link LENGTH_GUESS_STRETCH} times the length, on the
   *  wire size chosen for the stated one. */
  allowanceUsedIfLonger: number;
  /** True when the stretched run no longer fits — this length decides the size. */
  sensitive: boolean;
};

export function computeLengthSensitivity(
  system: System,
  defLookup: DefLookup = getDefinition,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): Map<string, LengthSensitivity> {
  const specOf = makeDiagnosticCableSpecGetter(system, defLookup, autoSizeTargetPct);

  const out = new Map<string, LengthSensitivity>();
  for (const cable of system.cables) {
    const spec = specOf(cable);
    // Guard the division: a zero allowance would rank every cable as sensitive.
    const allowanceUsed =
      spec.vdropAllowancePct > 0 ? spec.vDropPct / spec.vdropAllowancePct : 0;
    const allowanceUsedIfLonger = allowanceUsed * LENGTH_GUESS_STRETCH;
    out.set(cable.id, {
      allowanceUsed,
      allowanceUsedIfLonger,
      sensitive: allowanceUsedIfLonger > 1,
    });
  }
  return out;
}
